/**
 * Web Push subscription.
 *
 * **The iOS caveat, which is the single most common support question:** Safari
 * delivers Web Push only to a PWA the user has added to the home screen. In a
 * Safari *tab*, `Notification.requestPermission` is not even available, so the
 * captain cannot subscribe at all -- and if they somehow do, nothing arrives.
 * `pushSupport()` below detects exactly that case and returns
 * `'ios-needs-install'`, which the onboarding screen turns into "Share → Add to
 * Home Screen" instructions rather than a generic failure.
 *
 * Push is a courtesy layer. The in-app notification list is the system of
 * record, and every decision is visible there whether or not a notification was
 * ever delivered. A captain who declines notifications is not locked out of
 * anything.
 */

import { api } from './api'

export type PushSupport =
  | 'ready'
  | 'granted'
  | 'denied'
  | 'ios-needs-install'
  | 'unsupported'

export function isIos(): boolean {
  const ua = navigator.userAgent
  // iPadOS 13+ reports itself as a Mac; the touch-point count is what gives it
  // away, and it matters because iPad is a real captain device.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Non-standard, iOS Safari only, and the only reliable signal there.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function pushSupport(): PushSupport {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return isIos() && !isStandalone() ? 'ios-needs-install' : 'unsupported'
  }
  if (!('Notification' in window)) {
    return isIos() && !isStandalone() ? 'ios-needs-install' : 'unsupported'
  }
  if (isIos() && !isStandalone()) return 'ios-needs-install'
  if (Notification.permission === 'granted') return 'granted'
  if (Notification.permission === 'denied') return 'denied'
  return 'ready'
}

/**
 * Ask for permission and register with the server.
 *
 * Returns false when the captain declined -- a normal outcome, not an error.
 * Only call this from a user gesture: browsers ignore (and Safari penalises) a
 * permission prompt fired on page load.
 */
export async function subscribe(): Promise<boolean> {
  const support = pushSupport()
  if (support !== 'ready' && support !== 'granted') return false

  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission()
  if (permission !== 'granted') return false

  const { public_key: publicKey, configured } = await api.get<{
    public_key: string
    configured: boolean
  }>('/api/push/key')
  if (!configured || !publicKey) {
    // Push is not set up on this deployment. Not the captain's problem, and not
    // worth an error dialog -- the in-app list still works.
    return false
  }

  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  // A key rotation invalidates old subscriptions. Rather than let the browser
  // keep a subscription the server can no longer sign for, drop and re-create.
  if (existing && !sameKey(existing, publicKey)) await existing.unsubscribe()

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }))

  const json = subscription.toJSON()
  await api.post('/api/push/subscribe', {
    endpoint: subscription.endpoint,
    keys: json.keys ?? {},
  })
  return true
}

export async function unsubscribe(): Promise<void> {
  const registration = await navigator.serviceWorker?.ready
  const subscription = await registration?.pushManager.getSubscription()
  // Tell the server first, while we still know the endpoint. Unsubscribing
  // locally first would leave a row the server can never match again.
  await api
    .post('/api/push/unsubscribe', { endpoint: subscription?.endpoint ?? null })
    .catch(() => undefined)
  await subscription?.unsubscribe()
}

export async function isSubscribed(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false
  const registration = await navigator.serviceWorker.ready
  return Boolean(await registration.pushManager.getSubscription())
}

function sameKey(subscription: PushSubscription, publicKey: string): boolean {
  const current = subscription.options.applicationServerKey
  if (!current) return false
  const a = new Uint8Array(current)
  const b = urlBase64ToUint8Array(publicKey)
  return a.length === b.length && a.every((byte, i) => byte === b[i])
}

/** VAPID keys travel as base64url; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const raw = atob(padded)
  // Built over an explicit ArrayBuffer, and the return type says so. Since
  // TypeScript 5.7 `Uint8Array` is generic over its backing buffer, and the
  // default `Uint8Array<ArrayBufferLike>` is not assignable to `BufferSource`
  // -- it might be backed by a SharedArrayBuffer. `applicationServerKey`
  // rejected it, which would have failed at the first `subscribe()` call.
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
  return out
}
